import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { Select, Button, Modal as AntModal, Spin, Empty, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';

// Temel bir öğenin sahip olması gereken minimum alanlar
export interface BaseSelectItem {
  _id: string; // Veya genel bir ID tipi, örn: string | number
  name: string; // Veya genel bir etiket alanı
  [key: string]: any; // Diğer özelliklere izin ver
}

interface NewSelectorProps<TItem extends BaseSelectItem> {
  selectedItem: TItem | null;
  onItemSelected: (item: TItem | null) => void; // Seçimi temizlemek için null kabul et
  fetchItems: () => Promise<TItem[]>; // Öğeleri getiren asenkron fonksiyon
  createItemComponent: React.FC<{ // Modal içinde yeni öğe oluşturma formunu render edecek bileşen
    isDrawerMode?: boolean; // Stil tutarlılığı için (opsiyonel)
    onSuccess: (newItem: TItem) => void; // Başarılı oluşturma sonrası callback
    onCancel: () => void; // İptal callback'i
  }>;
  itemKeyField: keyof TItem; // Select.Option için key ve value olarak kullanılacak alan (örn: '_id')
  itemLabelField: keyof TItem; // Select.Option içinde etiket olarak gösterilecek alan (örn: 'name')
  searchFields: (keyof TItem)[]; // Arama/filtreleme için kullanılacak alanlar
  renderItemOption?: (item: TItem) => ReactNode; // Select.Option içeriğini özel olarak render etmek için fonksiyon
  modalTitle: string; // Yeni öğe oluşturma modalının başlığı
  placeholder?: string;
  className?: string;
  createButtonText?: string; // Dropdown içindeki "Yeni Ekle" butonunun metni
  allowClear?: boolean; // Seçimi temizleme butonunu göster/gizle
}

const NewSelector = <TItem extends BaseSelectItem>({
  selectedItem,
  onItemSelected,
  fetchItems,
  createItemComponent: CreateItemComponent,
  itemKeyField,
  itemLabelField,
  searchFields,
  renderItemOption,
  modalTitle,
  placeholder = "Öğe seçin veya arayın",
  className,
  createButtonText = "Yeni Öğe Ekle",
  allowClear = false,
}: NewSelectorProps<TItem>) => {
  const [items, setItems] = useState<TItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedItems = await fetchItems();
      setItems(fetchedItems || []);
    } catch (error) {
      console.error("Öğeler yüklenirken hata oluştu:", error);
      toast.error('Öğeler yüklenirken bir hata oluştu.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [fetchItems]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleItemCreated = async (newItem: TItem) => {
    setIsModalOpen(false);
    try {
      await loadItems(); // Listeyi yenile
      onItemSelected(newItem); // Yeni öğeyi seç
      toast.success(`Yeni öğe "${String(newItem[itemLabelField])}" seçildi.`);
    } catch (error) {
      console.error("Yeni öğe oluşturulduktan sonra liste güncellenirken veya öğe seçilirken hata:", error);
      toast.error('Yeni öğe seçilirken bir sorun oluştu.');
    }
  };

  const filteredItems = items.filter(item => {
    if (!searchTerm.trim()) return true;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return searchFields.some(field => {
      const fieldValue = item[field];
      return fieldValue != null && String(fieldValue).toLowerCase().includes(lowerSearchTerm);
    });
  });

  // Select.Option için varsayılan render fonksiyonu
  const defaultRenderItemOption = (item: TItem) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {/* Renk seçiciye özel hexCode gösterimi (genel kullanım için renderItemOption tercih edilir) */}
      {itemKeyField === '_id' && item.hexCode && typeof item.hexCode === 'string' && (
         <Tag color={`#${item.hexCode}`} style={{ marginRight: 8, minWidth: '20px', height: '20px', border: '1px solid #d9d9d9' }}>
           &nbsp;
         </Tag>
       )}
      <span>{String(item[itemLabelField])}</span>
      {/* Diğer genel bilgileri göstermek için burası genişletilebilir veya renderItemOption prop'u kullanılabilir */}
    </div>
  );

  return (
    <>
      <Select
        showSearch
        allowClear={allowClear}
        value={selectedItem ? (selectedItem[itemKeyField] as string | number) : undefined}
        onChange={(value) => {
          if (value === undefined && allowClear) { // Ant Design'da allowClear seçildiğinde value undefined olur
            onItemSelected(null);
            return;
          }
          const item = items.find(i => i[itemKeyField] === value);
          if (item) {
            onItemSelected(item);
          }
        }}
        onSearch={setSearchTerm}
        placeholder={placeholder}
        loading={loading}
        className={className || 'w-full'}
        filterOption={false} // Kendi filtrelememizi kullanıyoruz
        dropdownRender={(menu) => (
          <>
            {menu}
            <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8, borderTop: '1px solid #f0f0f0' }}>
              <Button type="link" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)} style={{ flex: 'auto' }}>
                {createButtonText}
              </Button>
            </div>
          </>
        )}
        notFoundContent={loading ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Öğe bulunamadı" />}
      >
        {filteredItems.map(item => (
          <Select.Option 
            key={String(item[itemKeyField])} 
            value={item[itemKeyField] as string | number} 
            label={String(item[itemLabelField])} // Arama için label
          >
            {renderItemOption ? renderItemOption(item) : defaultRenderItemOption(item)}
          </Select.Option>
        ))}
      </Select>

      <AntModal
        title={modalTitle}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null} // CreateItemComponent kendi butonlarını içermeli
        destroyOnClose // Modal kapandığında içindeki state'leri sıfırla
        width={600} // İsteğe bağlı olarak prop yapılabilir
      >
        <CreateItemComponent
          isDrawerMode // Modal içinde olduğunu belirtmek için (opsiyonel)
          onSuccess={handleItemCreated}
          onCancel={() => setIsModalOpen(false)}
        />
      </AntModal>
    </>
  );
};

export default NewSelector;
